<template>
  <div class="container mx-auto px-4 py-6">
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
      <!-- Card Header -->
      <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 class="text-xl font-semibold text-white text-center">
          {{ $t('Mobile.MobileQuantities.title') }}
        </h2>
      </div>

      <!-- Form Content -->
      <div class="p-6">
        <form
          class="space-y-6"
          @submit.prevent="saveForm"
        >
          <!-- Date Field -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileQuantities.date') }}
            </label>

            <AppInput
              v-model="quantityForm.form.day"
              class="form-input"
              type="date"
              name="date"
              data-test="select-day-input"
              :error="quantityForm.getErrors('day')"
            />
          </div>

          <!-- WBS Code Field -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileQuantities.wbsCode') }}
            </label>

            <AppSelect
              v-model.number="quantityForm.form.wbs_code_id"
              class="form-input"
              data-test="wbs_code"
              name="wbs_code_id"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.code }))"
              :error="quantityForm.getErrors('wbs_code_id')"
            />
          </div>

          <!-- WBS Description Field -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileQuantities.wbsDescription') }}
            </label>

            <AppSelect
              v-model.number="quantityForm.form.wbs_code_id"
              class="form-input"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.description }))"
              name="wbs_description"
              data-test="wbs_description"
              :error="quantityForm.getErrors('wbs_code_id')"
            />
          </div>

          <!-- Capture By Section -->
          <div class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileQuantities.captureBy') }}
            </label>

            <div class="flex space-x-3">
              <button
                class="flex-1 btn border-2"
                :class="[
                  quantityForm.form.capture_loads
                    ? 'btn--secondary-default'
                    : 'btn--primary-blue border-transparent'
                ]"
                type="button"
                @click="toggleCaptureQuantity"
              >
                {{ $t('Mobile.MobileQuantities.quantity') }}
              </button>

              <button
                class="flex-1 btn border-2"
                :class="[
                  quantityForm.form.capture_loads
                    ? 'btn--primary-blue border-transparent'
                    : 'btn--secondary-default'
                ]"
                type="button"
                @click="toggleCaptureLoads"
              >
                {{ $t('Mobile.MobileQuantities.loads') }}
              </button>
            </div>
          </div>

          <!-- Loads Section (Conditional) -->
          <template v-if="quantityForm.form.capture_loads === true">
            <!-- Conversion Type -->
            <div class="space-y-3">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('Mobile.MobileQuantities.conversion') }}
              </label>

              <div class="flex space-x-3">
                <button
                  class="flex-1 btn border-2"
                  :class="[
                    quantityForm.form.conversion_type === 'tonnes'
                      ? 'btn--primary-blue border-transparent'
                      : 'btn--secondary-default'
                  ]"
                  type="button"
                  @click="quantityForm.form.conversion_type = 'tonnes'"
                >
                  {{ $t('Mobile.MobileQuantities.tonnes') }}
                </button>

                <button
                  class="flex-1 btn border-2"
                  :class="[
                    quantityForm.form.conversion_type === 'cubes'
                      ? 'btn--primary-blue border-transparent'
                      : 'btn--secondary-default'
                  ]"
                  type="button"
                  @click="quantityForm.form.conversion_type = 'cubes'"
                >
                  {{ $t('Mobile.MobileQuantities.cubes') }}
                </button>
              </div>
            </div>

            <!-- Asset Types -->
            <template
              v-for="(assetType, i) in quantityForm.form.asset_types"
              :key="i"
            >
              <div class="bg-gray-50 rounded-lg p-4 space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">
                      {{ $t('Mobile.MobileQuantities.assetType') }}
                    </label>

                    <AppSelect
                      v-model.number="assetType.asset_type_id"
                      class="form-input pr-8"
                      data-test="asset_type_id"
                      name="asset_type_id[]"
                      required
                      :options="assetTypes.map((assetType) => ({ value: assetType.id, label: assetType.name}))"
                      :error="quantityForm.getErrors(`asset_types.${i}.asset_type_id`)"
                    />
                  </div>

                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">
                      {{ $t('Mobile.MobileQuantities.loads') }}
                    </label>

                    <AppInput
                      v-model.number="assetType.loads"
                      class="form-input"
                      name="loads[]"
                      type="number"
                      min="0"
                      required
                      data-test="loads"
                    />
                  </div>
                </div>

                <div class="flex bg-white rounded-lg p-3 border border-gray-200">
                  <div class="flex flex-col border-r border-gray-200 w-full pr-3">
                    <div class="flex justify-between">
                      <p class="text-sm text-gray-600">
                        {{ $t('Mobile.MobileQuantities.conversionPerLoad') }}
                      </p>

                      <p class="font-medium text-gray-900">
                        {{ assetType.asset_type_id ? conversionForAsset(assetType.asset_type_id) : '-' }}
                      </p>
                    </div>

                    <div class="flex justify-between">
                      <p class="text-sm text-gray-600">
                        {{ $t('Mobile.MobileQuantities.totalConversion') }}
                      </p>

                      <p class="font-medium text-blue-600">
                        {{ assetType.asset_type_id ? assetType.loads * conversionForAsset(assetType.asset_type_id) : '-' }}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="pl-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    @click="removeAssetType(i)"
                  >
                    <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
                  </button>
                </div>
              </div>
            </template>

            <!-- Add Asset Load Button -->
            <button
              class="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center space-x-2"
              type="button"
              @click="addAssetType"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <span class="font-medium">{{ $t('Mobile.MobileQuantities.addAssetLoad') }}</span>
            </button>
          </template>

          <!-- Quantity Field -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileQuantities.quantity') }} {{ wbsCodeUnit ? `(${wbsCodeUnit})` : '' }}
            </label>

            <AppInput
              v-model.number="quantityForm.form.quantity"
              name="quantity"
              type="number"
              min="0"
              data-test="quantity"
              :error="quantityForm.getErrors('quantity')"
              :disabled="quantityForm.form.capture_loads"
              class="form-input disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>

          <!-- Surveyed Quantity (Conditional) -->
          <div
            v-if="selectedQuantity && selectedQuantity.bias"
            class="bg-blue-50 rounded-lg p-4 border border-blue-200"
          >
            <label class="flex items-center justify-between">
              <span class="text-sm font-medium text-blue-900">
                {{ $t('Mobile.MobileQuantities.surveyedQuantity') }} {{ wbsCodeUnit ? `(${wbsCodeUnit})` : '' }}
              </span>

              <input
                type="number"
                class="form-input w-32 bg-white border-blue-300 text-blue-900"
                disabled
                :value="selectedQuantity.quantity + selectedQuantity.bias"
              >
            </label>
          </div>

          <!-- Action Buttons -->
          <div class="flex space-x-4 pt-6">
            <button
              type="button"
              class="btn btn--secondary-blue flex-1"
              @click.prevent="router.push({ name: 'MobileProjectPage' })"
            >
              {{ $t('Mobile.MobileQuantities.back') }}
            </button>

            <button
              v-if="projectsStore.project?.unarchived"
              type="submit"
              class="btn btn--primary-blue flex-1"
              :disabled="queryQuantityAction.state.value === States.LOADING"
              data-test="submit_form"
            >
              {{ quantityId ? $t('Mobile.MobileQuantities.update') : $t('Mobile.MobileQuantities.save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useDayQueryParam from '@/composables/useDayQueryParam';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateAsYMD } from '@/helpers';
import { t } from '@/i18n';
import { useAssetTypesStore } from '@/store/assetTypes';
import { useDailyQuantitiesStore } from '@/store/dailyQuantities';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', project.value?.id).get();
});

const project = computed(() => {
  return projectsStore.project;
});

const dailyQuantitiesStore = useDailyQuantitiesStore();
const router = useRouter();
const queryDay = useDayQueryParam();
const queryQuantityAction = useStoreApiAction(dailyQuantitiesStore.queryQuantity);

const quantityForm = useFormData({
  day: dateAsYMD(queryDay.value),
  wbs_code_id: undefined,
  quantity: 0,
  asset_types: [] as { asset_type_id: number; loads: number }[],
  capture_loads: false,
  conversion_type: 'tonnes',
});

const addAssetType = () => {
  quantityForm.form.asset_types.push({
    asset_type_id: undefined,
    loads: 0,
  });
};

const removeAssetType = (index: number) => {
  quantityForm.form.asset_types.splice(index, 1);
};

watch(() => quantityForm.form.day, (newValue) => {
  if(newValue) {
    router.replace({ query: { day: newValue } });
  }
}, { immediate: true });

const quantityId = ref();

const selectedQuantity = computed(() => {
  return dailyQuantitiesStore.models.with('quantity_loads').find(quantityId.value);
});

watch(selectedQuantity, (newValue) => {
  if(newValue) {
    if(newValue.quantity_loads.length > 0) {
      quantityForm.form.capture_loads = true;

      quantityForm.form.asset_types = newValue.quantity_loads.map((load) => ({
        asset_type_id: load.asset_type_id,
        loads: load.loads,
      }));

      quantityForm.form.conversion_type = newValue.quantity_loads[0].load_unit;
    }

    quantityForm.form.quantity = newValue.quantity;
  }
});

watch(() => props.projectId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    wbsCodesStore.index(newValue).then((codes) => {
      quantityForm.form.wbs_code_id = codes[0]?.id;
    });
  }
}, { immediate: true });

watch(() => [quantityForm.form.wbs_code_id, quantityForm.form.day], (newValue) => {
  if(newValue) {
    queryQuantityAction.request(projectsStore.project?.id, quantityForm.form).then(({ id }) => {
      quantityId.value = id;
    }).catch((error) => {
      if(error.status === 404) {
        quantityForm.resetData([
          'quantity',
          'conversion_type',
          'capture_loads',
        ]);

        quantityForm.form.asset_types = [];

        quantityId.value = undefined;
      }
    });
  }
}, { immediate: true });

const storeQuantityAction = useStoreApiAction(dailyQuantitiesStore.createQuantity);
const updateQuantityAction = useStoreApiAction(dailyQuantitiesStore.updateQuantity);

const saveForm = () => {
  quantityForm.resetErrors();

  if(quantityId.value) {
    if(quantityForm.form.capture_loads) {
      quantityForm.form.asset_types = quantityForm.form.asset_types.map((assetType) => ({
        asset_type_id: assetType.asset_type_id,
        load_unit: quantityForm.form.conversion_type,
        loads: assetType.loads,
      }));
    } else {
      quantityForm.form.asset_types = [];
    }

    updateQuantityAction.request(quantityId.value, quantityForm.form).then(() => {
      Swal.fire({
        icon: 'success',
        text: t('Mobile.MobileQuantities.quantityUpdated'),
      }).then(() => {
        router.push({ name: 'MobileProjectPage' });
      });
    }).catch((error) => {
      quantityForm.setErrors(error.data);
    });
  } else {
    if(quantityForm.form.asset_types.length > 0) {
      quantityForm.form.asset_types = quantityForm.form.asset_types.map((assetType) => ({
        asset_type_id: assetType.asset_type_id,
        load_unit: quantityForm.form.conversion_type,
        loads: assetType.loads,
      }));
    } else {
      quantityForm.form.asset_types = undefined;
    }

    storeQuantityAction.request(projectsStore.project?.id, quantityForm.form).then((quantity) => {
      Swal.fire({
        icon: 'success',
        text: t('Mobile.MobileQuantities.quantitySaved'),
      }).then(() => {
        router.push({ name: 'MobileProjectPage' });
      });

      quantityId.value = quantity.id;
    }).catch((error) => {
      quantityForm.setErrors(error.data);
    });
  }
};

const wbsCodeUnit = computed(() => {
  return wbsCodesStore.models.where('id', quantityForm.form.wbs_code_id).first()?.unit;
});

const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage' };

const assetTypesStore = useAssetTypesStore();

const assetTypes = computed(() => {
  return assetTypesStore.models.where('project_id', project.value?.id).get();
});

watch(() => [quantityForm.form.asset_types, quantityForm.form.conversion_type], (newValue) => {
  if(newValue && Array.isArray(newValue[0])) {
    let totalQuantity = 0;

    newValue[0].forEach((element) => {
      const assetType = assetTypesStore.models.find(element.asset_type_id);

      if(assetType) {
        if(quantityForm.form.conversion_type === 'tonnes') {
          totalQuantity += element.loads * assetType.tonnes_per_load;
        } else {
          totalQuantity += element.loads * assetType.cubes_per_load;
        }
      }
    });

    quantityForm.form.quantity = totalQuantity;
  }
}, { deep: true });

watch(() => quantityForm.form.capture_loads, (newValue, oldValue) => {
  if(!newValue && oldValue && !quantityId.value) {
    quantityForm.form.asset_types = [];
  }
});

const conversionForAsset = (id: number) => {
  const assetType = assetTypes.value.find((value) => value.id === id);

  return quantityForm.form.conversion_type === 'cubes' ? assetType.cubes_per_load : assetType.tonnes_per_load;
};

const toggleCaptureQuantity = () => {
  if(quantityForm.form.asset_types.length > 0) {
    Swal.fire({
      icon: 'warning',
      title: t('Mobile.MobileQuantities.confirmTitle'),
      text: t('Mobile.MobileQuantities.switchToQuantityWarning'),
      showCancelButton: true,
    }).then((result) => {
      if(result.isConfirmed) {
        quantityForm.form.capture_loads = false;
        quantityForm.form.asset_types = [];
      }
    });
  } else {
    quantityForm.form.capture_loads = false;
  }
};

const toggleCaptureLoads = () => {
  if(quantityForm.form.quantity > 0) {
    Swal.fire({
      icon: 'warning',
      title: t('Mobile.MobileQuantities.confirmTitle'),
      text: t('Mobile.MobileQuantities.switchToLoadsWarning'),
      showCancelButton: true,
    }).then((result) => {
      if(result.isConfirmed) {
        quantityForm.form.capture_loads = true;
        quantityForm.form.quantity = 0;
      }
    });
  } else {
    quantityForm.form.capture_loads = true;
  }
};
</script>

<style scoped></style>
